import Image from "next/image";

import { cn } from "@/lib/utils";

import type { ComparisonCompetitor, ComparisonFeature } from "@/content/alternatives/schema";

interface ComparisonTableProps {
  heading: string;
  competitors: ComparisonCompetitor[];
  features: ComparisonFeature[];
}

export function ComparisonTable({ heading, competitors, features }: ComparisonTableProps) {
  const colCount = competitors.length + 1;

  return (
    <section className="bg-ash-100/60 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="mb-12 text-center font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
          {heading}
        </h2>

        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            {/* Best Value badge row */}
            <div
              className="grid"
              style={{ gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${competitors.length}, 1fr)` }}
            >
              <div />
              {competitors.map((c, i) => (
                <div key={c.name} className="flex justify-center pb-2">
                  {i === 0 && (
                    <span className="rounded-full bg-rebolt-500 px-4 py-1.5 text-xs font-semibold text-white">
                      Best Value
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Header row with logos */}
            <div
              className="grid border-b border-ash-200"
              style={{ gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${competitors.length}, 1fr)` }}
            >
              <div className="flex items-center px-4 py-4 text-sm font-medium text-ash-500">Features</div>
              {competitors.map((c, i) => (
                <div
                  key={c.name}
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-2 px-3 py-4",
                    i === 0 && "font-semibold"
                  )}
                >
                  {i === 0 && <div className="absolute inset-0 rounded-t-xl bg-rebolt-500/4" />}
                  <div className="relative z-10 flex flex-col items-center gap-2">
                    {c.logo && (
                      <Image
                        src={c.logo.src}
                        alt={c.logo.alt}
                        width={24}
                        height={24}
                        className="size-6 object-contain"
                      />
                    )}
                    <span className="text-sm font-semibold text-ash-900">{c.name}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Feature rows */}
            {features.map((feature, rowIdx) => (
              <div
                key={feature.name}
                className={cn("grid border-b border-ash-200 last:border-b-0", rowIdx % 2 === 0 && "bg-ash-100/80")}
                style={{
                  gridTemplateColumns: `minmax(140px, 1.2fr) repeat(${colCount - 1}, 1fr)`,
                }}
              >
                <div className="flex items-center px-4 py-3.5 text-sm text-ash-600">{feature.name}</div>
                {feature.values.map((value, colIdx) => (
                  <div
                    key={`${feature.name}-${colIdx}`}
                    className={cn(
                      "relative flex items-center justify-center px-3 py-3.5 text-center text-sm text-ash-700",
                      colIdx === 0 && "font-semibold text-ash-900"
                    )}
                  >
                    {colIdx === 0 && <div className="absolute inset-0 bg-rebolt-500/4" />}
                    <span className="relative z-10">{value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
